/**
 * API Client Configuration
 * Dynamically uses the correct API base URL based on environment
 */

// In development: Empty string (uses Vite proxy)
// In UAT/production: https://your-azure-gateway-url
export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

/**
 * Make an API request with automatic environment-aware base URL
 */
export async function apiRequest<T = any>(
  endpoint: string,
  options?: RequestInit
): Promise<T> {
  const url = endpoint.startsWith('http') 
    ? endpoint 
    : `${API_BASE_URL}${endpoint}`;

  // Get auth token and test persona headers
  const token = localStorage.getItem('access_token')
  const devPersonaStr = localStorage.getItem('dev_selected_persona')
  
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }
  
  // Add Authorization header if token exists
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }
  
  // Add test persona headers if in dev mode
  if (devPersonaStr) {
    try {
      const devPersona = JSON.parse(devPersonaStr)
      headers['X-Test-User-Id'] = devPersona.personnelNumber || 'TEST001'
      headers['X-Test-User-Roles'] = (devPersona.roles || ['employee']).join(',')
      if (devPersona.permissions?.length) {
        headers['X-Test-User-Permissions'] = devPersona.permissions.join(',')
      }
    } catch (e) {
      console.warn('Failed to parse dev persona for headers')
    }
  }

  const response = await fetch(url, {
    ...options,
    headers: {
      ...headers,
      ...options?.headers,
    },
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`API Error: ${response.status} ${response.statusText} - ${errorText}`);
  }

  return response.json();
}

/**
 * API endpoints
 */
/** Why an access change is made, recorded in the access-change trail. */
export type AccessChangeNote = { reason: string; reference?: string | null };

/** One entry of a person's access-change trail. */
export interface AccessChange {
  occurred_at: string | null;
  change: string;
  role_name: string | null;
  permission_key: string | null;
  scope_type: string | null;
  scope_value: string | null;
  actor: string;
  /** 'app' (User Management), or 'database' for a system-admin role changed outside it. */
  via: string;
  reason: string | null;
  reference: string | null;
  change_set_id: string | null;
}

export const API = {
  employees: {
    sync: () => apiRequest('/api/v1/integration/employees/sync', { method: 'POST' }),
    search: (query: string) => apiRequest(`/api/v1/integration/employees/search?query=${encodeURIComponent(query)}`),
  },
  users: {
    list: async (params?: { 
      page?: number; 
      page_size?: number; 
      search?: string;
      campus?: string; 
      department?: string; 
      is_active?: boolean;
      role?: string;
    }) => {
      const queryParams = new URLSearchParams();
      if (params?.page) queryParams.append('page', params.page.toString());
      if (params?.page_size) queryParams.append('page_size', params.page_size.toString());
      if (params?.search) queryParams.append('search', params.search);
      if (params?.campus) queryParams.append('campus', params.campus);
      if (params?.department) queryParams.append('department', params.department);
      if (params?.is_active !== undefined) queryParams.append('is_active', params.is_active.toString());
      if (params?.role) queryParams.append('role', params.role);
      
      const url = `/api/v1/identity/users${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      return apiRequest<{
        users: any[];
        total: number;
        page: number;
        page_size: number;
        total_pages: number;
      }>(url);
    },
    get: async (userId: string) => {
      return apiRequest<any>(`/api/v1/identity/users/${userId}`);
    },
    getRoles: async (userId: string) => {
      return apiRequest(`/api/v1/identity/users/${userId}/roles`);
    },
    assignRole: async (userId: string, roleName: string, change?: AccessChangeNote) =>
      apiRequest(`/api/v1/identity/users/${userId}/roles/assign`, {
        method: 'POST',
        body: JSON.stringify({ role_name: roleName, ...change })
      }),
    unassignRole: async (userId: string, roleName: string, change?: AccessChangeNote) =>
      apiRequest(`/api/v1/identity/users/${userId}/roles/unassign`, {
        method: 'POST',
        body: JSON.stringify({ role_name: roleName, ...change })
      }),
    /** Every role, grant, limit and area this person was given or lost, newest first. */
    getAccessHistory: async (userId: string) =>
      apiRequest<{ user_id: string; entries: AccessChange[] }>(
        `/api/v1/identity/users/${userId}/access-history`
      ),
    // ── Performance-cycle exclusion ────────────────────────────────────────
  getReviewExclusion: async (userId: string) =>
    apiRequest<{ user_id: string; review_excluded: boolean; reason: string | null; excluded_by: string | null; excluded_at: string | null }>(
      `/api/v1/identity/users/${userId}/review-exclusion`
    ),
  /** Exclude from (or return to) objectives, check-ins and calibration.
   *  A reason is required when excluding. */
  updateReviewExclusion: async (userId: string, reviewExcluded: boolean, reason?: string) =>
    apiRequest<{ user_id: string; review_excluded: boolean; reason: string | null; excluded_by: string | null; excluded_at: string | null }>(
      `/api/v1/identity/users/${userId}/review-exclusion`,
      { method: 'PUT', body: JSON.stringify({ review_excluded: reviewExcluded, reason: reason || null }) }
    ),

  // ── Direct per-user capability grants ──────────────────────────────────
  /** The grantable capability catalogue (served from the backend registry, so
   *  the client never keeps its own copy of the key list). */
  getPermissionCatalog: async () =>
    apiRequest<{ permissions: { key: string; label: string; description: string; group: string }[] }>(
      `/api/v1/identity/users/permissions/catalog`
    ),
  getUserPermissions: async (userId: string) =>
    apiRequest<{ user_id: string; permissions: string[] }>(
      `/api/v1/identity/users/${userId}/permissions`
    ),
  /** Full-replace set semantics, same as HRBP functional areas. */
  updateUserPermissions: async (userId: string, permissions: string[], change?: AccessChangeNote) =>
    apiRequest<{ user_id: string; permissions: string[] }>(
      `/api/v1/identity/users/${userId}/permissions`,
      { method: 'PUT', body: JSON.stringify({ permissions, ...change }) }
    ),

  // ── Department scopes on a grant ───────────────────────────────────────
  /** A key absent from `scopes` is organisation-wide. `scopable_keys` lists the
   *  keys the server can actually filter — never offer a scope outside it, or
   *  the UI would promise a restriction the endpoints ignore. */
  getUserPermissionScopes: async (userId: string) =>
    apiRequest<{
      user_id: string;
      scopes: { permission_key: string; scope_type: string; scope_values: string[] }[];
      scopable_keys: string[];
    }>(`/api/v1/identity/users/${userId}/permission-scopes`),

  /** Full-replace. An empty scope_values for a key REMOVES its scope, widening
   *  that grant back to the whole organisation. */
  updateUserPermissionScopes: async (
    userId: string,
    scopes: { permission_key: string; scope_type: string; scope_values: string[] }[],
    change?: AccessChangeNote,
  ) =>
    apiRequest<{
      user_id: string;
      scopes: { permission_key: string; scope_type: string; scope_values: string[] }[];
      scopable_keys: string[];
    }>(`/api/v1/identity/users/${userId}/permission-scopes`, {
      method: 'PUT',
      body: JSON.stringify({ scopes, ...change }),
    }),

  /** Department codes in live use, with position counts, for the scope picker. */
  getDepartmentScopeOptions: async () =>
    apiRequest<{ departments: { code: string; positions: number; vacant: number }[] }>(
      `/api/v1/identity/users/permission-scopes/department-options`
    ),

  /** Financial dimension campus codes in live use, for the campus scope picker. */
  getCampusScopeOptions: async () =>
    apiRequest<{ campuses: { code: string; people: number; mostly: string | null }[] }>(
      `/api/v1/identity/users/permission-scopes/campus-options`
    ),

  /** The functions, and the departments within each, a talent dashboard grant can be
   *  narrowed to. Save `value`: the function, or 'Function › Department'. */
  getTalentAreaScopeOptions: async () =>
    apiRequest<{
      functions: {
        name: string;
        value: string;
        positions: number;
        departments: { name: string; value: string; positions: number }[];
      }[];
    }>(`/api/v1/identity/users/permission-scopes/talent-area-options`),

  getHRBPFunctionalAreas: async (userId: string, roleName: string = 'hrbp') =>
      apiRequest<{ user_id: string; functional_areas: string[]; role_name: string }>(
        `/api/v1/identity/users/${userId}/hrbp-functional-areas?role_name=${roleName}`
      ),
    updateHRBPFunctionalAreas: async (
      userId: string, functionalAreas: string[], roleName: string = 'hrbp', change?: AccessChangeNote,
    ) =>
      apiRequest<{ user_id: string; functional_areas: string[]; role_name: string }>(
        `/api/v1/identity/users/${userId}/hrbp-functional-areas`,
        { method: 'PUT', body: JSON.stringify({ functional_areas: functionalAreas, role_name: roleName, ...change }) }
      ),
    resetOnboarding: async (userId: string) =>
      apiRequest(`/api/v1/identity/users/${userId}/onboarding/reset`, {
        method: 'POST'
      }),
    deactivate: async (userId: string) =>
      apiRequest(`/api/v1/identity/users/${userId}`, {
        method: 'DELETE'
      }),
    updateManager: async (userId: string, managerId: string | null) =>
      apiRequest(`/api/v1/identity/users/${userId}/manager`, {
        method: 'PATCH',
        body: JSON.stringify({ manager_id: managerId })
      }),
  },
  roles: {
    list: async () => {
      return apiRequest<Array<{
        id: string;
        name: string;
        display_name: string;
        description: string | null;
      }>>('/api/v1/identity/roles/');  // Add trailing slash to match FastAPI route
    },
    create: async (data: { name: string; display_name: string; description?: string }) => {
      return apiRequest('/api/v1/identity/roles/', {  // Add trailing slash
        method: 'POST',
        body: JSON.stringify(data),
      });
    }
  },
  // Add other endpoints as needed
} as const;
